/**
 *  BÉRJEGYZÉK (a munkabér írásbeli elszámolása, Mt. 155. § (3)) – a Számfejtés „Bérlap” gombja és a
 *  Nyomtatványok › Havi bérjegyzék közös lapja.
 *
 *  Tartalomban az RLB bérjegyzékét követi (user, 2026-11-05: „a bérjegyzék/bérlap tartalma ilyennek kellene
 *  hogy legyen, ne ebben a formátumban, és a 0-kat nem kell kiírni”): alapadatok, időadatok, naptár a napi
 *  jelenléti kódokkal, szabadságkeret, adóköteles kifizetések, levonások, SZJA-részletezés a kedvezményekkel,
 *  családi járulékkedvezmény, munkáltatói közterhek, összesítő (járandóság / levonás / hóközi / kifizetendő).
 *  Elrendezésben a saját, két hasábos A4-es lapunk; a NULLA értékű sor és az üres blokk kimarad.
 *
 *  Adat: ber.js › GET /berlap/:id (számfejtés + tételek + jelenlét + naptár + napi kódok + szabadság +
 *  távolléti díj). A tételek kategóriái: jovedelem | levonas | kedvezmeny | munkaltatoi | informacio.
 */
import { datum, esc } from './dokumentum-nyomtatas';

/*  A BÉRJEGYZÉK KAPCSOLHATÓ BLOKKJAI (user, 2026-11-05: „a naptár és a munkáltató által fizetendő adatok legyenek
 *  kapcsolhatóak, alapból bekapcsolva”). A beállítás ebben a böngészőben / asztali programban marad meg, és a
 *  Béradatok Bérlap gombja meg a Nyomtatványok › Havi bérjegyzék is ezt használja. */
export interface BerjegyzekOpciok { naptar: boolean; munkaltato: boolean; }
const OPCIO_KULCS = 'berjegyzek-opciok';
export function berjegyzekOpciok(): BerjegyzekOpciok {
  try {
    const o = JSON.parse(localStorage.getItem(OPCIO_KULCS) || '{}');
    return { naptar: o.naptar !== false, munkaltato: o.munkaltato !== false };
  } catch { return { naptar: true, munkaltato: true }; }
}
export function berjegyzekOpciokMent(o: BerjegyzekOpciok): void {
  try { localStorage.setItem(OPCIO_KULCS, JSON.stringify({ naptar: !!o.naptar, munkaltato: !!o.munkaltato })); } catch { /* privát mód */ }
}

/** Egész forint, ezres tagolással a négyjegyűeknél is (a hu-HU locale az 5000-et nem tagolja). */
const ft = (v: any): string => {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return '';
  return (n < 0 ? '−' : '') + String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
};

const HONAP = ['január', 'február', 'március', 'április', 'május', 'június', 'július', 'augusztus', 'szeptember', 'október', 'november', 'december'];
const N = (v: any) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
/** Óra / nap egy tizedesre, magyar tizedesvesszővel; 0 → ''. */
const t1 = (v: any) => { const n = Math.round(N(v) * 10) / 10; return n ? String(n).replace('.', ',') : ''; };
const szaz = (v: any) => (v == null || v === '' ? '' : `${String(Math.round(N(v) * 100) / 100).replace('.', ',')}%`);

type Sor = [string, any, any?];   // címke, érték, (kiegészítő oszlop)

/** Címke–érték lista; a 0 / üres értékű sor kimarad. `penz`: Ft-ként formáz. */
function kv(sorok: Sor[], penz = true, kiemelt: string[] = []): string {
  const lathato = sorok.filter(([, v]) => v != null && v !== '' && (typeof v !== 'number' || Math.round(v) !== 0));
  if (!lathato.length) return '';
  return `<table class="bj-kv">${lathato.map(([k, v, x]) => `<tr class="${kiemelt.includes(k) ? 'bj-ossz' : ''}">
    <td>${esc(k)}</td><td class="bj-x">${esc(x ?? '')}</td><td class="num">${esc(typeof v === 'number' && penz ? ft(v) : v)}</td></tr>`).join('')}</table>`;
}

const blokk = (cim: string, tartalom: string) => (tartalom ? `<div class="bj-blokk"><h3>${esc(cim)}</h3>${tartalom}</div>` : '');

/* A napi jelenléti kódok (ber-sulinform.js › STATUSZOK) és a naptári napok jelei. */
const KOD: Record<number, [string, string]> = {
  1: ['D', 'ledolgozott nap'], 2: ['Sz', 'fizetett szabadság'], 3: ['B', 'betegség (betegszabadság / táppénz)'],
  4: ['K', 'kiküldetés'], 5: ['F', 'fizetés nélküli távollét'], 6: ['M', 'mentesülés (fizetett)'], 7: ['H', 'távmunka / home office'],
};

function naptarHtml(d: any): { html: string; munkaszuneti: number; db: Record<number, number> } {
  const nt = d.naptar; const napok = d.napok || {};
  const db: Record<number, number> = {};
  if (!nt?.napok?.length) return { html: '', munkaszuneti: 0, db };
  const munkaszuneti = nt.napok.filter((n: any) => !n.munkanap && n.hetnap !== 0 && n.hetnap !== 6).length;
  if (!Object.keys(napok).length) return { html: '', munkaszuneti, db };
  const hasznalt = new Set<string>();
  const cella = (n: any) => {
    const st = napok[n.datum] == null ? null : Number(napok[n.datum]);
    let jel = '', cls = '';
    if (st && KOD[st]) { jel = KOD[st][0]; db[st] = (db[st] || 0) + 1; hasznalt.add(String(st)); }
    else if (!n.munkanap) { jel = n.nev && n.hetnap !== 0 && n.hetnap !== 6 ? 'Ü' : 'P'; hasznalt.add(jel); }
    else { jel = '–'; hasznalt.add('–'); }
    if (!n.munkanap) cls = 'pihen';
    return `<td class="${cls}"><span class="nap">${n.nap}</span><b>${jel}</b></td>`;
  };
  //  Hétfővel kezdődő hetek.
  const elso = (nt.napok[0].hetnap + 6) % 7;
  const cellak = [...Array(elso).fill('<td class="ures"></td>'), ...nt.napok.map(cella)];
  while (cellak.length % 7) cellak.push('<td class="ures"></td>');
  const sorok: string[] = [];
  for (let i = 0; i < cellak.length; i += 7) sorok.push(`<tr>${cellak.slice(i, i + 7).join('')}</tr>`);
  const jelmagyarazat = [
    ...Object.keys(KOD).filter((k) => hasznalt.has(k)).map((k) => `<span><b>${KOD[+k][0]}</b> ${KOD[+k][1]}</span>`),
    hasznalt.has('P') ? '<span><b>P</b> pihenőnap</span>' : '',
    hasznalt.has('Ü') ? '<span><b>Ü</b> munkaszüneti nap</span>' : '',
    hasznalt.has('–') ? '<span><b>–</b> nincs rögzítve (ledolgozottnak számít)</span>' : '',
  ].filter(Boolean).join('');
  return {
    html: `<table class="bj-naptar"><thead><tr>${['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'].map((x) => `<th>${x}</th>`).join('')}</tr></thead>
      <tbody>${sorok.join('')}</tbody></table><div class="bj-jm">${jelmagyarazat}</div>`,
    munkaszuneti, db,
  };
}

export function berjegyzekLap(d: any, opciok: BerjegyzekOpciok = berjegyzekOpciok()): string {
  const s = d.szamfejtes || {}; const a = d.alkalmazott || {}; const u = d.ugyfel || {}; const j = d.jogviszony || {};
  const jel = d.jelenlet || {}; const szab = d.szabadsag;
  const tet = (k: string) => (d.tetelek || []).filter((t: any) => t.kategoria === k);
  const napiOra = (() => { const h = N(j.heti_oraszam) || 40; const n = N(j.napi_oraszam); return n > 0 && Math.abs(n * 5 - h) < 0.01 ? n : h / 5; })();
  const nt = naptarHtml(d);
  const idoszak = s.tol && s.ig ? `${datum(s.tol)} – ${datum(s.ig)}` : `${s.ev}. ${HONAP[N(s.ho) - 1] || ''}`;
  const cim = (x: any) => [x.irsz, x.telepules, x.cim].filter(Boolean).join(' ') || x.cim || '';

  /* ── Alapadatok ── */
  const berforma = j.ber_tipus === 'ora' ? 'időbér – órabér' : (j.ber_tipus === 'teljesitmeny' ? 'teljesítménybér' : 'időbér – havibér');
  const alapadatok = kv([
    ['Bérforma', berforma],
    [j.ber_tipus === 'ora' ? 'Órabér (Ft/óra)' : 'Besorolási bér (Ft/hó)', N(j.alapber)],
    ['Távolléti díj (Ft/óra)', N(d.tavolleti_oradij) && j.ber_tipus !== 'ora'
      ? (() => { const c = Math.round(N(d.tavolleti_oradij) * 100); return `${ft(Math.floor(c / 100))},${String(c % 100).padStart(2, '0')}`; })() : ''],
    ['Munkaidő', j.munkarend === 'nincs' ? 'munkaidő nélkül' : `heti ${t1(N(j.heti_oraszam) || 40)} óra, napi ${t1(napiOra)} óra${j.munkarend === 'egyenlotlen' ? ' (egyenlőtlen)' : ''}`],
    ['Munkakör', [j.munkakor, j.feor ? `FEOR ${j.feor}` : ''].filter(Boolean).join(' – ')],
    ['Jogviszony kezdete', j.kezdet ? datum(j.kezdet) : ''],
  ]);

  /* ── Időadatok (nap / óra) ── */
  const nap = (ora: any) => (N(ora) ? t1(N(ora) / napiOra) : '');
  const ido = (k: string, napV: any, oraV: any): Sor => [k, [napV ? `${napV} nap` : '', oraV ? `${oraV} óra` : ''].filter(Boolean).join(' / ')];
  const idoadatok = kv([
    ido('A hónap munkanapjai', t1(jel.munkanapok || d.naptar?.munkanapok), ''),
    ido('Ledolgozott idő', t1(jel.ledolgozott_napok), t1(jel.ledolgozott_orak)),
    ido(' – ebből távmunka / home office', t1(nt.db[7]), ''),
    ido(' – ebből kiküldetés', t1(nt.db[4]), ''),
    ido('Munkaszüneti nap', t1(nt.munkaszuneti), ''),
    ido('Fizetett szabadság', nap(jel.szabadsag_orak), t1(jel.szabadsag_orak)),
    ido('Betegszabadság', nap(jel.betegszabadsag_orak), t1(jel.betegszabadsag_orak)),
    ido('Táppénz (keresőképtelenség)', t1(jel.tappenz_napok), ''),
    ido('Fizetett (igazolt) távollét', nap(jel.fizetett_tavollet), t1(jel.fizetett_tavollet)),
    ido('Fizetés nélküli távollét', nap(jel.fizetes_nelkuli_orak), t1(jel.fizetes_nelkuli_orak)),
    ido('Rendkívüli munkaidő (túlóra)', '', t1(jel.tullora)),
    ido('Éjszakai munka', '', t1(jel.ejszakai)),
    ido('Vasárnapi munka', '', t1(jel.vasarnapi)),
    ido('Műszakpótlékos munka', '', t1(jel.muszakpotlek)),
  ], false);

  /* ── Szabadság ── */
  const szabadsag = szab ? kv([
    ['Éves szabadság (nap)', t1(szab.keret), szab.elozo_evi ? `ebből előző évi ${t1(szab.elozo_evi)}` : ''],
    ['Ebben a hónapban kivett', t1(szab.ehavi)],
    ['Eddig felhasznált (a hónap végéig)', t1(szab.felhasznalt)],
    ['Még kivehető', N(szab.maradek) ? t1(szab.maradek) : (N(szab.keret) ? '0' : '')],
    ['Betegszabadság keret / felhasznált', N(szab.beteg_felhasznalt) ? `${t1(szab.beteg_keret)} / ${t1(szab.beteg_felhasznalt)}` : ''],
  ], false, ['Még kivehető']) : '';

  /* ── Adóköteles kifizetések (a jövedelem-tételek; a 10–17. sor alapja óra) ── */
  const jov = tet('jovedelem');
  const jovSorok: Sor[] = jov.map((t: any) => {
    const sr = N(t.sorrend);
    const orasT = sr >= 10 && sr <= 17 && t.alap != null && N(t.alap) > 0;
    const menny = orasT ? `${t1(t.alap)} óra${sr === 10 || sr >= 15 ? ` / ${t1(N(t.alap) / napiOra)} nap` : ''}`
      : (t.kulcs != null && t.alap != null ? `${szaz(t.kulcs)} × ${ft(t.alap)}` : '');
    return [t.megnevezes, N(t.osszeg), menny];
  });
  const jovOssz = jov.reduce((x: number, t: any) => x + N(t.osszeg), 0);
  const tbTetel = tet('levonas').find((t: any) => /^Társadalombiztosítási járulék/.test(t.megnevezes));
  const kifizetesek = kv([...jovSorok,
    ['Bruttó járandóság', jovOssz],
    ['Járulékalap', tbTetel && Math.round(N(tbTetel.alap)) !== Math.round(jovOssz) ? N(tbTetel.alap) : 0],
  ], true, ['Bruttó járandóság']);

  /* ── Levonások ── */
  const lev = tet('levonas');
  const csjk = tet('kedvezmeny').find((t: any) => /^Családi járulékkedvezmény/.test(t.megnevezes));
  const levSorok: Sor[] = lev.map((t: any) => {
    const alapKulcs = t.alap != null && t.kulcs != null ? `${szaz(t.kulcs)} × ${ft(t.alap)}` : '';
    return [t.megnevezes, N(t.osszeg), alapKulcs];
  });
  const levOssz = lev.reduce((x: number, t: any) => x + N(t.osszeg), 0);
  const levonasok = kv([...levSorok, ['Levonások összesen', levOssz]], true, ['Levonások összesen']);

  /* ── SZJA-részletezés (kedvezmények) ── */
  const szjaT = lev.find((t: any) => /^Személyi jövedelemadó/.test(t.megnevezes));
  const kedv = tet('kedvezmeny').filter((t: any) => t !== csjk);
  const mentes = kedv.filter((t: any) => /fiatal|anyák|NÉTAK/.test(t.megnevezes)).reduce((x: number, t: any) => x + N(t.alap), 0);
  const hokoziT = tet('informacio').find((t: any) => N(t.sorrend) === 900);
  const adokoteles = !hokoziT && szjaT ? N(szjaT.alap) + mentes + N(s.egyeb_kedvezmeny) + N(s.csaladi_kedvezmeny) : 0;
  const kedvSorok: Sor[] = kedv.map((t: any) => {
    if (/^Családi kedvezmény/.test(t.megnevezes)) {
      return [t.megnevezes.replace(/, adóalap\)/, ')'), N(s.csaladi_kedvezmeny) || N(t.alap), N(s.csaladi_kedvezmeny) && N(s.csaladi_kedvezmeny) < N(t.alap) ? `keret ${ft(t.alap)}` : ''];
    }
    return [t.megnevezes.replace(/ \(adóalap\)$/, ''), N(t.alap), /Személyi|Első házas/.test(t.megnevezes) ? 'havi keret' : ''];
  });
  const szja = kv([
    ['Adóköteles jövedelem', adokoteles],
    ...kedvSorok,
    ['Adóelőleg alapja', szjaT ? N(szjaT.alap) : N(s.adoalap)],
    ['Fizetendő SZJA-előleg', szjaT ? N(szjaT.osszeg) : N(s.szja)],
  ], true, ['Fizetendő SZJA-előleg']);

  const jarKedv = csjk ? kv([
    ['Fel nem használt családi kedvezmény', N(csjk.alap)],
    ['Érvényesítve a TB-járulékból (15%)', Math.abs(N(csjk.osszeg))],
    ['TB-járulék a kedvezmény előtt', tbTetel ? N(tbTetel.osszeg) + Math.abs(N(csjk.osszeg)) : 0],
  ]) : '';

  /* ── Munkáltatói közterhek ── */
  const mk = tet('munkaltatoi');
  const info = tet('informacio').filter((t: any) => N(t.sorrend) !== 900);
  const munkaltato = kv([
    ...mk.map((t: any): Sor => [t.megnevezes, N(t.osszeg), t.alap != null && t.kulcs != null ? `${szaz(t.kulcs)} × ${ft(t.alap)}` : '']),
    ...info.map((t: any): Sor => [t.megnevezes, N(t.osszeg) || N(t.alap), '']),
    ['Munkáltatói összköltség', N(s.koltseg)],
  ], true, ['Munkáltatói összköltség']);

  /* ── Összesítő ── */
  const hokozi = hokoziT ? Math.abs(N(hokoziT.osszeg)) : 0;
  const netto = N(s.netto);
  const atutalas = !!a.bankszamla;
  const vegso = kv([
    ['Összes járandóság', jovOssz],
    ['Összes levonás', levOssz],
    ['Hóközi kifizetés (előleg)', hokozi],
    [atutalas ? 'Átutalt összeg' : 'Készpénzben kifizetve', netto, atutalas ? a.bankszamla : ''],
  ], true, []);

  return `
    <div class="fej"><div><h1>Bérjegyzék – ${esc(s.ev)}. ${esc(HONAP[N(s.ho) - 1] || '')}</h1>
      <div class="alcim">Elszámolt időszak: ${esc(idoszak)}${s.tipus && s.tipus !== 'havi' ? ` · ${esc(s.megnevezes || s.tipus)}` : ''}${s.allapot === 'veglegesitett' ? '' : ' · TERVEZET'}</div></div>
      <div class="jobb">${s.kifizetes ? `Kifizetés napja: <b>${esc(datum(s.kifizetes))}</b>` : ''}</div></div>
    <div class="bj-felek">
      <div><b>${esc(a.nev || '')}</b>${a.adoazonosito ? ` (${esc(a.adoazonosito)})` : ''}<br>${esc(cim(a))}${a.taj ? `<br>TAJ: ${esc(a.taj)}` : ''}</div>
      <div class="jobb"><b>${esc(u.hivatalos_nev || u.nev || '')}</b>${u.adoszam ? ` (${esc(u.adoszam)})` : ''}<br>${esc(cim(u))}</div>
    </div>
    <div class="bj-racs">
      <div class="bj-hasab">
        ${blokk('Alapadatok', alapadatok)}
        ${blokk('Időadatok', idoadatok)}
        ${opciok.naptar ? blokk('Naptár', nt.html) : ''}
        ${blokk('Szabadság', szabadsag)}
      </div>
      <div class="bj-hasab">
        ${blokk('Adóköteles kifizetések', kifizetesek)}
        ${blokk('Levonások', levonasok)}
        ${blokk('Személyi jövedelemadó', szja)}
        ${blokk('Családi járulékkedvezmény', jarKedv)}
        ${opciok.munkaltato ? blokk('Munkáltató által fizetendő (tájékoztatás)', munkaltato) : ''}
      </div>
    </div>
    <div class="bj-vegso">${vegso}
      <div class="kiemelt"><span>Kifizetendő</span><span>${ft(netto)} Ft</span></div></div>
    ${N(s.letiltas) > 0 ? `<div class="megj">Nettó letiltás nélkül: <b>${ft(netto + N(s.letiltas))} Ft</b>, ebből végrehajtási letiltás: ${ft(s.letiltas)} Ft.</div>` : ''}
    <div class="megj">A munkabér írásbeli elszámolása a Munka Törvénykönyve 155. § (3) bekezdése szerint.</div>
    <div class="atvetel"><div class="vonal">a bérjegyzéket átvettem (munkavállaló)</div></div>`;
}
